<template>
  <div data-testid="import-result" class="flex flex-col py-6 gap-6">
    <div class="flex flex-col items-center gap-2">
      <v-icon
        :icon="imported > 0 ? mdiCheckCircleOutline : mdiAlertCircleOutline"
        size="56"
        :class="imported > 0 ? 'text-feedBackSuccess' : 'text-feedBackAlert'"
      />
      <p class="text-textPrimary text-lg font-bold">
        {{ imported > 0 ? 'Importação concluída!' : 'Nenhuma validação importada' }}
      </p>
    </div>

    <div class="flex justify-center gap-8">
      <div v-if="imported > 0" class="flex flex-col items-center gap-1">
        <span data-testid="import-result-imported" class="text-2xl font-bold text-feedBackSuccess">
          {{ imported.toLocaleString('pt-BR') }}
        </span>
        <span class="text-sm text-neutralLight">importadas</span>
      </div>
      <div v-if="rejected > 0" class="flex flex-col items-center gap-1">
        <span data-testid="import-result-rejected" class="text-2xl font-bold text-feedBackAlert">
          {{ rejected.toLocaleString('pt-BR') }}
        </span>
        <span class="text-sm text-neutralLight">rejeitadas</span>
      </div>
      <div v-if="mergedDuplicates > 0" class="flex flex-col items-center gap-1">
        <span
          data-testid="import-result-merged-duplicates"
          class="text-2xl font-bold text-primary30"
        >
          {{ mergedDuplicates.toLocaleString('pt-BR') }}
        </span>
        <span class="text-sm text-neutralLight">duplicatas mescladas</span>
      </div>
    </div>

    <div v-if="errors.length > 0" class="border rounded-lg overflow-clip border-lightGray30">
      <div
        class="bg-gray-50 px-4 py-2 border-b border-lightGray30 flex items-center justify-between"
      >
        <span class="text-sm font-semibold text-textPrimary">Linhas rejeitadas</span>
        <outlined-button
          data-testid="export-rejected-button"
          text="Exportar"
          width="auto"
          height="32px"
          :prepend-icon="mdiDownload"
          class="!border-primary30 !text-primary30 !text-xs !px-3"
          @click="handleExportRejected"
        />
      </div>
      <div class="max-h-[400px] overflow-y-auto overflow-x-auto">
        <table class="w-full text-sm" aria-label="Linhas rejeitadas na importação">
          <thead class="sticky top-0 bg-gray-50 border-b border-lightGray30">
            <tr>
              <th class="text-left px-4 py-2 text-neutralLight font-medium w-16">Linha</th>
              <th class="text-left px-4 py-2 text-neutralLight font-medium w-28">CRM</th>
              <th class="text-left px-4 py-2 text-neutralLight font-medium w-16">UF</th>
              <th class="text-left px-4 py-2 text-neutralLight font-medium">Motivo</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(error, index) in sortedErrors"
              :key="index"
              :data-testid="`import-error-row-${index}`"
              class="border-b last:border-b-0 border-lightGray30"
            >
              <td class="text-left px-4 py-2 text-textPrimary">{{ error.row }}</td>
              <td class="text-left px-4 py-2 text-textPrimary font-mono">{{ error.crm || '—' }}</td>
              <td class="text-left px-4 py-2 text-textPrimary">{{ error.uf || '—' }}</td>
              <td class="text-left px-4 py-2 text-textPrimary">{{ error.message }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="errorsTruncated" class="bg-feedBackAlert/10 px-4 py-2 border-t border-lightGray30">
        <span class="text-xs text-feedBackAlert">
          Existem mais linhas rejeitadas que não foram exibidas.
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiCheckCircleOutline, mdiAlertCircleOutline, mdiDownload } from '@mdi/js'

import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import type { ImportReportError } from '@/Network/Types/Responses/QualificationPolicyResponses'

const props = defineProps<{
  imported: number
  rejected: number
  mergedDuplicates: number
  errors: ImportReportError[]
  errorsTruncated: boolean
  policyId: number
  policyName: string
}>()

const sortedErrors = computed(() => [...props.errors].sort((a, b) => a.row - b.row))

function escapeCsvField(value: string): string {
  let escaped = value
  if (/^[=+\-@]/.test(escaped)) escaped = `'${escaped}`
  if (escaped.includes('"') || escaped.includes(';') || escaped.includes('\n')) {
    return `"${escaped.replace(/"/g, '""')}"`
  }
  return escaped
}

function handleExportRejected() {
  const BOM = '\uFEFF'
  const header = 'Linha;CRM;UF;Motivo'
  const rows = props.errors.map(
    (e) =>
      `${e.row};${escapeCsvField(e.crm || '')};${escapeCsvField(e.uf || '')};${escapeCsvField(e.message)}`
  )
  const csv = BOM + [header, ...rows].join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const now = new Date()
  const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}h${String(now.getMinutes()).padStart(2, '0')}`
  const slug = props.policyName
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  link.setAttribute('download', `rejeitados-#${props.policyId}-${slug}-${timestamp}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
</script>
